/**
 * Shared presentational contracts for the Find Your Agent surface (Phase 31).
 *
 * The `/agents` page owns the canonical filter/sort/page state (in the URL) and
 * passes it down; the search bar, filters, results grid, and card are all
 * controlled/presentational and emit changes through callbacks. Keeping these
 * shapes in one server-safe file (no `'use client'`, no imports) lets every
 * component + the SSR page share one definition without a client-boundary taint.
 */

/** A single controlled-dropdown option. `value` is the id used for filtering. */
export interface AgentFilterOption {
  /** The standard-term id (specialty/language) or synced-location id (area). */
  value: string;
  /** Human-readable label rendered in the dropdown + active-filter chip. */
  label: string;
}

/** The org's enabled filter subsets — the ONLY source of dropdown options. */
export interface AgentFilterSubsets {
  specialties: AgentFilterOption[];
  languages: AgentFilterOption[];
  serviceAreas: AgentFilterOption[];
}

/** Fail closed for audit and test labels that must never reach public filters. */
export function isPublicTaxonomyLabel(label: string): boolean {
  return !/\b(?:disabled|test(?:ing)?)\b/i.test(label);
}

export type AgentSortOption = "recommended" | "name";

/**
 * Full controlled filter/sort state. Empty-string ids mean "all" (no filter);
 * the page maps non-empty ids into the `searchAgents` params and the URL query.
 */
export interface AgentFilterState {
  q: string;
  specialtyId: string;
  languageId: string;
  serviceAreaId: string;
  hasOpenHouses: boolean;
  hasReviews: boolean;
  sort: AgentSortOption;
}

export const EMPTY_AGENT_FILTER_STATE: AgentFilterState = {
  q: "",
  specialtyId: "",
  languageId: "",
  serviceAreaId: "",
  hasOpenHouses: false,
  hasReviews: false,
  sort: "recommended",
};

// AgentMapPoint (the never-populated agent map-view seam) was removed with the
// results map toggle in the 2026-06-12 v1 styling pass — `synced_locations`
// never grew coordinates, so the view could only ever render its fallback note.

/** True when any narrowing filter is active (the search text is tracked apart). */
export function hasActiveFilters(state: AgentFilterState): boolean {
  return Boolean(
    state.specialtyId ||
      state.languageId ||
      state.serviceAreaId ||
      state.hasOpenHouses ||
      state.hasReviews,
  );
}

// ---------------------------------------------------------------------------
// URL ⇄ state — the ONE definition of the /agents wire format. The page reads
// it (`agentFilterFromSearchParams`) and the client orchestrator writes it
// (`agentFilterToQuery`); keeping both halves here means a new facet changes the
// query keys + encodings in exactly one place (SSR ↔ back/forward stay in sync).
// ---------------------------------------------------------------------------

type RawSearchParams = Record<string, string | string[] | undefined>;

function firstValue(value: string | string[] | undefined): string {
  return Array.isArray(value) ? (value[0] ?? "") : (value ?? "");
}

/** Serialize (filterState, page) → query params, omitting defaults for short URLs. */
export function agentFilterToQuery(
  state: AgentFilterState,
  page: number,
): URLSearchParams {
  const params = new URLSearchParams();
  if (state.q) params.set("q", state.q);
  if (state.specialtyId) params.set("specialty", state.specialtyId);
  if (state.languageId) params.set("language", state.languageId);
  if (state.serviceAreaId) params.set("area", state.serviceAreaId);
  if (state.hasOpenHouses) params.set("open_houses", "1");
  if (state.hasReviews) params.set("reviews", "1");
  if (state.sort !== "recommended") params.set("sort", state.sort);
  if (page > 1) params.set("page", String(page));
  return params;
}

/** Parse the query back into filter state + page (the read side, SSR). */
export function agentFilterFromSearchParams(sp: RawSearchParams): {
  filterState: AgentFilterState;
  page: number;
} {
  const requestedPage = Number(firstValue(sp.page) || "1");
  return {
    filterState: {
      q: firstValue(sp.q).trim(),
      specialtyId: firstValue(sp.specialty),
      languageId: firstValue(sp.language),
      serviceAreaId: firstValue(sp.area),
      hasOpenHouses: firstValue(sp.open_houses) === "1",
      hasReviews: firstValue(sp.reviews) === "1",
      sort: firstValue(sp.sort) === "name" ? "name" : "recommended",
    },
    page: Number.isSafeInteger(requestedPage) ? Math.max(1, requestedPage) : 1,
  };
}
